import * as turf from '@turf/turf';
import { Coord } from '@turf/turf';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { AUsers } from '../../atoms/global/users';
import { PrimaryColor, SidelyBlack } from '../../styles/global/css/Utils';
import { Owner } from '../orders/model/Model';
import { MapSettingsContext } from './context';
import { UserCenter } from './Map';
import type { MapLayerWorker } from './mapLayerWorker';
import { Point } from './model';

export type LayerStyle = {
	fillColor: string;
	fillOpacity: number;
	strokeColor: string;
	strokeWeight: number;
};

const defaultMapLayerStyle = {
	fillColor: 'transparent',
	fillOpacity: 0.25,
	strokeColor: 'transparent',
	strokeWeight: 1,
};

let drawnPolys: { [key: number]: GoogleMapType } | null;

/**
 * Create polygons from a list of points
 * @param  {boolean} available draw polys or not
 * @param  {Point[]} points points to use to create polygons
 * @param  map map to draw on
 * @param  maps ???
 */
export function useUsersPoly(points: Point[], map, maps) {
	const { userPolyModeHook } = React.useContext(MapSettingsContext);
	const [available] = userPolyModeHook;
	const owners = useRecoilValue(AUsers);

	function clearDrawnPolys() {
		if (drawnPolys) {
			Object.values(drawnPolys).forEach((poly) => poly.setMap(null));
			drawnPolys = null;
		}
	}

	function setMap(map) {
		if (drawnPolys) {
			Object.values(drawnPolys).forEach((poly) => poly.setMap(map));
		}
	}

	React.useEffect(() => {
		if (!maps || !map) return;
		clearDrawnPolys();
		const pointsPerUsers = points.reduce((acc, point) => {
			const ownerId = point.properties.detail.ownerId ?? -1;
			if (!acc.has(ownerId)) acc.set(ownerId, []);
			acc.get(ownerId)!.push(turf.point(point.geometry.coordinates));
			return acc;
		}, new Map());
		drawnPolys = {};
		for (const [ownerId, geos] of pointsPerUsers.entries()) {
			const pointsCollection = turf.featureCollection(geos);
			const path = turf.convex(pointsCollection)?.geometry.coordinates[0];
			if (!path) continue;
			const owner = owners.find(owner => owner.id === ownerId);
			drawnPolys[ownerId] = new maps.Polygon({
				map: available ? map : null,
				path: path.map(p => ({ lat: p[1], lng: p[0] })),
				strokeColor: owner?.color ?? PrimaryColor,
				strokeOpacity: 0.5,
				strokeWeight: 2,
				fillColor: owner?.color ?? PrimaryColor,
				fillOpacity: 0.25
			});
		}
	}, [points, maps, map]);

	React.useEffect(() => {
		setMap(available ? map : null);
	}, [available]);
}

/**
 * Generate users addresses
 * @param  {boolean} available generate users addresses or not
 * @returns {UserCenter[] | undefined} list of user centers
 */
export function useUsersAddresses(available: boolean): UserCenter[] | undefined {
	const owners = useRecoilValue(AUsers);
	const centers: UserCenter[] = React.useMemo(() => {
		const centers: UserCenter[] = [];
		for (const owner of owners) {
			if (owner.latitude === undefined || owner.longitude === undefined) continue;
			centers.push({ id: owner.id, center: { lat: owner.latitude, lng: owner.longitude } });
		}
		return centers;
	}, [owners]);

	return available ? centers : undefined;
}

type GoogleMapType = any;
export type Boundary = 'region' | 'department'

function geometryToGeoJson(geometry) {
	const type = geometry.getType();

	const closeRing = coords => {
		const first = coords[0];
		const last = coords[coords.length - 1];
		if (first[0] !== last[0] || first[1] !== last[1]) {
			coords.push([...first]);
		}
		if (coords.length < 4) {
			while (coords.length < 4) coords.push([...first]);
		}
		return coords;
	};

	if (type === 'Polygon') {
		const rings = geometry
			.getArray()
			.map(ring => closeRing(ring.getArray().map(ll => [ll.lng(), ll.lat()])));
		return { type: 'Polygon', coordinates: rings };
	}

	if (type === 'MultiPolygon') {
		const polys = geometry.getArray().map(poly => {
			const arr = poly
				.getArray()
				.map(ring =>
					closeRing(ring.getArray().map(ll => [ll.lng(), ll.lat()]))
				);
			return arr;
		});
		return { type: 'MultiPolygon', coordinates: polys.map(p => p[0]) };
	}

	if (type === 'Point') {
		return { type: 'Point', coordinates: [geometry.lng(), geometry.lat()] };
	}

	console.warn('Unsupported geometry type:', type);
	return null;
}


function calcLayerStyle(layer, owners: Owner[], pointsPerUsers: Map<number, Coord[]>, key: Boundary, setLayerStyles: React.Dispatch<React.SetStateAction<Map<Boundary, LayerStyle[]>>>) {
	const geometries: unknown[] = [];
	layer.forEach((feature) => {
		const geometry = geometryToGeoJson(feature.getGeometry())?.coordinates;
		geometries.push(geometry);
	});
	if (geometries.length === 0) {
		return;
	}

	const worker = new Worker(new URL('./mapLayerWorker.ts', import.meta.url)) as MapLayerWorker;
	worker.postMessage({ geometries, owners, pointsPerUsers });
	worker.onmessage = (res) => {
		setLayerStyles((map) => {
			map.set(key, res.data);
			return new Map(map);
		});
		worker.terminate(); // Clean up the worker after execution
	};
}

function setLayerStyle(layer, key: Boundary, layerStyles: Map<Boundary, LayerStyle[]>, strokeBoundary: boolean, fillBoundary: boolean) {
	if (layerStyles.has(key)) {
		const style = layerStyles.get(key)!;
		let i = 0;
		layer.setStyle((_) => {
			const featureStyle = style[i++] ?? defaultMapLayerStyle;
			return {
				...featureStyle,
				fillColor: fillBoundary ? featureStyle.fillColor : 'transparent',
				strokeColor: strokeBoundary ? SidelyBlack : featureStyle.fillColor === 'transparent' ? 'transparent' : featureStyle.fillColor,
			};
		});
	} else {
		layer.setStyle((_) => defaultMapLayerStyle);
	}

}

/**
 * Create polygons for french regions / departments
 * @param  {boolean} available draw polys or not
 * @param  {Point[]} points points to use to create polygons
 * @param  map map to draw on
 * @param  maps ???
 */
export function useBoundariesPolys(points: Point[], map, maps) {

	const { strokeBoundaryModeHook, fillBoundaryModeHook } = React.useContext(MapSettingsContext);
	const [strokeBoundary] = strokeBoundaryModeHook;
	const [fillBoundary] = fillBoundaryModeHook;
	const owners = useRecoilValue(AUsers);
	const [layerStyles, setLayerStyles] = React.useState<Map<Boundary, LayerStyle[]>>(new Map());
	const [regionsLayer, setRegionsLayer] = React.useState<GoogleMapType | null>(null);
	const [departmentsLayer, setDepartmentsLayer] = React.useState<GoogleMapType | null>(null);

	const pointsPerUsers: Map<number, Coord[]> = React.useMemo(() => points.reduce((acc, point) => {
		const ownerId = point.properties.detail.ownerId ?? -1;
		if (!acc.has(ownerId))
			acc.set(ownerId, []);
		acc.get(ownerId)!.push(turf.point(point.geometry.coordinates));
		return acc;
	}, new Map()), [points]);

	React.useEffect(() => {
		return () => {
			setRegionsLayer(regionsLayer => {
				regionsLayer?.setMap(null);
				return null;
			});
			setDepartmentsLayer(departmentsLayer => {
				departmentsLayer?.setMap(null);
				return null;
			});
		};
	}, []);

	React.useEffect(() => {
		if (regionsLayer) {
			calcLayerStyle(regionsLayer, owners, pointsPerUsers, 'region', setLayerStyles);
		}
		if (departmentsLayer) {
			calcLayerStyle(departmentsLayer, owners, pointsPerUsers, 'department', setLayerStyles);
		}
	}, [points.length]);

	React.useEffect(() => {
		if (!map || !maps) return;
		function checkLayerAndSetStyle(boundary: Boundary, layer: GoogleMapType | null): GoogleMapType | null {
			if (!layer && (strokeBoundary === boundary || fillBoundary === boundary)) {
				layer = new maps.Data();
				layer.loadGeoJson(
					`${window.location.hostname === 'localhost' ? '' : '/public'}/boundaries/AMD${boundary === 'region' ? 1 : 2}_merged_europe.geojson`,
					undefined,
					(_) => {
						calcLayerStyle(layer, owners, pointsPerUsers, boundary, setLayerStyles);
					}
				);
			}
			if ((strokeBoundary === boundary || fillBoundary === boundary) && layer) {
				setLayerStyle(layer, boundary, layerStyles, strokeBoundary === boundary, fillBoundary === boundary);
				layer.setMap(map);
			} else {
				layer?.setMap(null);
			}
			return layer;
		}

		setRegionsLayer(checkLayerAndSetStyle('region', regionsLayer));
		setDepartmentsLayer(checkLayerAndSetStyle('department', departmentsLayer));
	}, [map, maps, strokeBoundary, layerStyles, fillBoundary]);

}
